import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../../contexts/WebSocketContext';
import useTheme from '../../hooks/useTheme';
import './Notification.css';

// SVG icons for different notification types
const icons = {
  success: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#4CAF50" strokeWidth="2"/>
      <path d="M7 12L10 15L17 8" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  error: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#F44336" strokeWidth="2"/>
      <path d="M8 8L16 16M8 16L16 8" stroke="#F44336" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  warning: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L2 21H22L12 2Z" stroke="#FF9800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 16V16.01M12 8V12" stroke="#FF9800" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  info: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#2196F3" strokeWidth="2"/>
      <path d="M12 7V7.01M12 11V17" stroke="#2196F3" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
  order: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#64B5F6" strokeWidth="2"/>
      <path d="M7 12L10 15L17 8" stroke="#64B5F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  default: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="11" stroke="#757575" strokeWidth="2"/>
      <path d="M12 7V7.01M12 11V17" stroke="#757575" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  )
};

/**
 * Format notification message with simple highlighting
 * @param {string} message - The notification message to format
 * @returns {JSX.Element} - Message with highlighted parts
 */
const formatNotificationMessage = (message) => {
  if (!message) return null;
  
  // Regex patterns for highlighting only (no content modification)
  const patterns = [
    // Symbol pattern
    { 
      regex: /\b([A-Z]{3,8})\b/g,
      className: 'notification-highlight'
    },
    // Price pattern - match decimal numbers
    { 
      regex: /\b(\d+\.\d+)\b/g,
      className: 'notification-highlight'
    },
    // Numbers for quantities, prices, etc.
    { 
      regex: /\b(\d+)\b/g,
      className: 'notification-highlight'
    },
    // Order type (buy, sell)
    {
      regex: /\b(buy|sell)\b/gi,
      className: 'notification-highlight'
    },
    // Leverage (e.g. 2x)
    {
      regex: /\b(\d+x)\b/g,
      className: 'notification-highlight'
    }
  ];
  
  // Function to highlight without modifying message content
  const highlightParts = () => {
    // Start with the whole message
    let parts = [{ text: message, isHighlight: false }];
    
    // Apply each pattern
    patterns.forEach(pattern => {
      const newParts = [];
      
      parts.forEach(part => {
        if (part.isHighlight) {
          // Already highlighted parts stay as they are
          newParts.push(part);
        } else {
          const textSegments = part.text.split(pattern.regex);
          
          for (let i = 0; i < textSegments.length; i++) {
            if (textSegments[i] === '') continue;
            
            const isMatch = i % 2 === 1; // Odd indices are matches in split result
            newParts.push({
              text: textSegments[i],
              isHighlight: isMatch,
              className: isMatch ? pattern.className : null
            });
          }
        }
      });
      
      parts = newParts;
    });
    
    // Convert parts to React elements
    return parts.map((part, index) => 
      part.isHighlight ? 
        <span key={index} className={part.className}>{part.text}</span> : 
        <span key={index}>{part.text}</span>
    );
  };
  
  return <>{highlightParts()}</>;
};

/**
 * The Close button component for notifications 
 */
const CloseButton = ({ onClick }) => (
  <button 
    className="notification-close" 
    onClick={onClick}
    aria-label="Close notification"
  >
    ×
  </button>
);

// Individual notification component
const NotificationItem = ({ notification, onClose }) => {
  const { message, type = 'info', status, notificationId, isExiting } = notification;
  const { theme } = useTheme();
  
  // Determine notification type - check if it's related to orders
  let notificationType = type;
  if (message && (message.toLowerCase().includes('order') || message.toLowerCase().includes('position'))) {
    notificationType = 'order';
  } else if (type) {
    notificationType = status === 'success' ? 'success' : 
                        status === 'error' ? 'error' :
                        status === 'warning' ? 'warning' : 
                        type;
  }
  
  // Get the icon based on notification type
  const icon = icons[notificationType] || icons.default;
  
  // Format the title based on status or type
  const title = status || 
                (notificationType === 'order' ? 'Order Update' : 
                notificationType.charAt(0).toUpperCase() + notificationType.slice(1));
  
  // Define animation class based on exit state
  const animationClass = isExiting ? 'notification-exiting' : '';
  
  // Apply theme class if theme is available
  const themeClass = theme ? `theme-${theme}` : '';
  
  return (
    <div className={`notification notification-${notificationType} ${animationClass} ${themeClass}`}>
      <div className="notification-icon">{icon}</div>
      <div className="notification-content">
        <div className="notification-title">
          <span>{title}</span>
        </div>
        <div className="notification-message">
          {formatNotificationMessage(message)}
        </div>
      </div>
      <CloseButton onClick={() => onClose(notificationId)} />
    </div>
  );
};

// Main notification container
const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const { on, off } = useWebSocket();
  const { theme } = useTheme();
  const timeoutsRef = useRef({});
  
  // Clear notification after animation completes
  const removeNotification = useCallback((id) => {
    // First mark as exiting to trigger animation
    setNotifications(prev => 
      prev.map(n => n.notificationId === id ? { ...n, isExiting: true } : n)
    );
    
    // Then actually remove after animation
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.notificationId !== id));
    }, 300); // Match animation duration
  }, []);
  
  // Auto-remove notifications after 7 seconds
  useEffect(() => {
    // Clean up old timeouts
    Object.values(timeoutsRef.current).forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = {};
    
    // Set new timeouts for all notifications
    notifications.forEach(notification => {
      if (!notification.isExiting && !timeoutsRef.current[notification.notificationId]) {
        timeoutsRef.current[notification.notificationId] = setTimeout(() => {
          removeNotification(notification.notificationId);
          // Remove from timeouts ref after triggered
          delete timeoutsRef.current[notification.notificationId];
        }, 7000);
      }
    });
    
    // Clean up on unmount
    return () => {
      Object.values(timeoutsRef.current).forEach(timeout => clearTimeout(timeout));
    };
  }, [notifications, removeNotification]);
  
  // Listen for new-notification events from WebSocket
  useEffect(() => {
    const handleNewNotification = (payload) => {
      if (payload && payload.data) {
        const { message, type, status, notificationId } = payload.data;
        
        // Log notification information to console
        console.log('🔔 New Notification Received:', {
          message,
          type,
          status,
          id: notificationId || 'Auto-generated ID',
          timestamp: new Date().toISOString()
        });
        
        // Add unique id if not provided
        const newNotification = {
          message,
          type,
          status,
          notificationId: notificationId || `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          isExiting: false
        };
        
        setNotifications(prev => [...prev, newNotification]);
      }
    };
    
    // Register event listener
    on('new-notification', handleNewNotification);
    
    // Clean up event listener on unmount
    return () => {
      off('new-notification', handleNewNotification);
    };
  }, [on, off]);
  
  if (notifications.length === 0) {
    return null;
  }
  
  // Apply theme class to container
  const themeClass = theme ? `theme-${theme}` : '';
  
  return (
    <div className={`notifications-container ${themeClass}`}>
      {notifications.map((notification) => (
        <NotificationItem 
          key={notification.notificationId} 
          notification={notification} 
          onClose={removeNotification} 
        />
      ))}
    </div>
  );
};

export default Notification; 